/** In-page UI strings (cards, empty states, counts) — Phase 17. */

export type UiCopy = { title: string; description: string };

export type UiTree = {
  count: {
    total: string;
    items: string;
    drafts: string;
    keywords: string;
    posts: string;
    companies: string;
    assets: string;
    videos: string;
    saved: string;
    recorded: string;
    defined: string;
  };
  card: {
    library: string;
    campaigns: string;
    reports: string;
    results: string;
    connectors: string;
    compose: string;
    watchlist: string;
    scripts: string;
    pages: string;
    workflows: string;
    newCampaign: string;
    crmLeadFromCampaign: string;
    newDraft: string;
    newPage: string;
    newVideo: string;
    newWorkflow: string;
    newImage: string;
    addCompetitor: string;
    generateReport: string;
    runAudit: string;
    auditHistory: string;
    keywordList: string;
    calendarQueue: string;
    byNetwork: string;
    scanUrl: string;
    runJob: string;
    recentJobs: string;
    openRecommendations: string;
    permissions: string;
    brandLocale: string;
    connectorAlerts: string;
    usageQuotas: string;
    leadsSpend: string;
    channelMix: string;
    conversionFunnel: string;
    last14Days: string;
    dailyPageViews: string;
    topPages: string;
    connectorHealth: string;
    leadVelocity: string;
    amosPageViews: string;
    amosSessions: string;
    topModule: string;
    crmLeads: string;
    agentConnectivity: string;
    auditEvents: string;
    searchResults: string;
    platformHealth: string;
    syncMix: string;
  };
  empty: {
    campaigns: UiCopy;
    ads: UiCopy;
    drafts: UiCopy;
    keywords: UiCopy;
    social: UiCopy;
    competitors: UiCopy;
    reports: UiCopy;
    audits: UiCopy;
    landing: UiCopy;
    images: UiCopy;
    videos: UiCopy;
    recommendations: UiCopy;
    jobs: UiCopy;
    workflows: UiCopy;
    observabilityAlerts: UiCopy;
    observabilityEvents: UiCopy;
    search: UiCopy;
    searchNone: UiCopy;
  };
  hint: {
    briefSkeleton: string;
    adsAgent: string;
    seoAgent: string;
    businessAgent: string;
    videoAgent: string;
    redisQueue: string;
    connectorsLive: string;
    alertsWebhook: string;
    awaitingScan: string;
    productAnalytics7d: string;
    sessionsTracked: string;
    mostViewedPath: string;
    leadVelocitySrc: string;
    oauthConnectors: string;
    moduleRegistry: string;
    noAlerts: string;
    navigateToCollect: string;
    searchMinChars: string;
  };
  form: {
    name: string;
    title: string;
    email: string;
    phone: string;
    role: string;
    tenant: string;
    organization: string;
    channel: string;
    channels: string;
    tone: string;
    brief: string;
    url: string;
    primaryKeyword: string;
    seedKeyword: string;
    company: string;
    industry: string;
    reportType: string;
    domain: string;
    pricingTier: string;
    notes: string;
    platform: string;
    topic: string;
    captionOptional: string;
    status: string;
    network: string;
    objective: string;
    dailyBudget: string;
    budgetUsd: string;
    format: string;
    slug: string;
    headline: string;
    template: string;
    websiteUrl: string;
    jobType: string;
    trigger: string;
    brandVoice: string;
    defaultLocale: string;
    timezone: string;
    alertEmail: string;
    alertWebhook: string;
    quietStart: string;
    quietEnd: string;
  };
  action: {
    save: string;
    saving: string;
    create: string;
    creating: string;
    createCampaign: string;
    generateDraft: string;
    generating: string;
    generateReport: string;
    runAudit: string;
    runResearch: string;
    researching: string;
    addCompetitor: string;
    createPost: string;
    createPage: string;
    createCreative: string;
    createScript: string;
    createWorkflow: string;
    scan: string;
    scanning: string;
    runJob: string;
    running: string;
    saveSettings: string;
    saveAlertPrefs: string;
    sendTestAlert: string;
    sending: string;
    flushDigest: string;
    flushing: string;
    runSchedulerTick: string;
    ticking: string;
    emailApprovals: string;
    quietHours: string;
    quietStrict: string;
    notifySyncFailures: string;
    markedAllRead: string;
    markedRead: string;
    createCrmTask: string;
    createCrmTaskDryRun: string;
    createCrmLead: string;
    createCrmLeadDryRun: string;
    cancel: string;
    crmWriteDisabled: string;
  };
  backlinks: {
    industry: string;
    country: string;
    allIndustries: string;
    allCountries: string;
    clearFilters: string;
    refresh: string;
    openSite: string;
    copyLink: string;
    copied: string;
    loading: string;
    more: string;
    allSites: string;
    someSites: string;
    hint: string;
    emptyTitle: string;
    emptyDescription: string;
    errorTitle: string;
    errorDescription: string;
  };
};

export const uiEn: UiTree = {
  count: {
    total: "{n} total",
    items: "{n} items",
    drafts: "{n} drafts",
    keywords: "{n} keywords",
    posts: "{n} posts",
    companies: "{n} companies",
    assets: "{n} assets",
    videos: "{n} videos",
    saved: "{n} saved",
    recorded: "{n} recorded",
    defined: "{n} defined",
  },
  card: {
    library: "Library",
    campaigns: "Campaigns",
    reports: "Reports",
    results: "Results",
    connectors: "Connectors",
    compose: "Compose",
    watchlist: "Watchlist",
    scripts: "Scripts",
    pages: "Pages",
    workflows: "Workflows",
    newCampaign: "New campaign",
    crmLeadFromCampaign: "CRM lead from campaign",
    newDraft: "New draft",
    newPage: "New page",
    newVideo: "New video",
    newWorkflow: "New workflow",
    newImage: "New image creative",
    addCompetitor: "Add competitor",
    generateReport: "Generate report",
    runAudit: "Run audit",
    auditHistory: "Audit history",
    keywordList: "Keyword list",
    calendarQueue: "Calendar queue",
    byNetwork: "By network",
    scanUrl: "Scan URL",
    runJob: "Run job",
    recentJobs: "Recent jobs",
    openRecommendations: "Open recommendations",
    permissions: "Your marketing permissions",
    brandLocale: "Brand & locale",
    connectorAlerts: "Connector alerts",
    usageQuotas: "Usage & quotas",
    leadsSpend: "Leads & spend",
    channelMix: "Channel mix",
    conversionFunnel: "Conversion funnel",
    last14Days: "Last 14 days",
    dailyPageViews: "Daily page views",
    topPages: "Top pages",
    connectorHealth: "Connector health",
    leadVelocity: "Lead velocity",
    amosPageViews: "MarketIQ page views",
    amosSessions: "MarketIQ sessions",
    topModule: "Top module",
    crmLeads: "CRM leads",
    agentConnectivity: "Agent connectivity",
    auditEvents: "Audit events",
    searchResults: "Search results",
    platformHealth: "Platform health",
    syncMix: "Sync mix",
  },
  empty: {
    campaigns: {
      title: "No campaigns yet",
      description: "Create a draft campaign to start orchestrating channels.",
    },
    ads: {
      title: "No ad campaigns",
      description: "Create a draft to generate creative copy.",
    },
    drafts: {
      title: "No drafts yet",
      description: "Generate your first piece from a title and brief.",
    },
    keywords: {
      title: "No keywords yet",
      description: "Run research with a seed term to generate a cluster.",
    },
    social: {
      title: "Queue empty",
      description: "Compose a post to populate the social calendar.",
    },
    competitors: {
      title: "No competitors yet",
      description: "Add a domain to start SEO and pricing comparison.",
    },
    reports: {
      title: "No reports yet",
      description: "Generate a SWOT or persona pack to get started.",
    },
    audits: {
      title: "No audits yet",
      description: "Audits recorded for this workspace will appear here.",
    },
    landing: {
      title: "No landing pages",
      description: "Generate an SEO-friendly outline with form CTA.",
    },
    images: {
      title: "No image creatives",
      description: "Create a brief to queue production.",
    },
    videos: {
      title: "No video scripts",
      description: "Generate a timed script from a brief.",
    },
    recommendations: {
      title: "No recommendations",
      description: "Activity across SEO, content, and ads will seed this feed.",
    },
    jobs: {
      title: "No jobs yet",
      description: "Run a CRM refresh or BI enrich job.",
    },
    workflows: {
      title: "No workflows",
      description: "Create an approval chain for content or social.",
    },
    observabilityAlerts: {
      title: "No connector alerts",
      description: "Sync warnings and validation issues will appear here.",
    },
    observabilityEvents: {
      title: "No audit events",
      description: "Creates, syncs, and write-backs are logged in amos_audit_events.",
    },
    search: {
      title: "Search MarketIQ",
      description: "Type at least 2 characters to search campaigns, keywords, drafts, and more.",
    },
    searchNone: {
      title: "No matches",
      description: "Try another term or create the asset in its module first.",
    },
  },
  hint: {
    briefSkeleton: "Brief → draft skeleton",
    adsAgent: "Ads Agent",
    seoAgent: "SEO Agent",
    businessAgent: "Business Agent",
    videoAgent: "Video Agent",
    redisQueue: "Redis queue when available",
    connectorsLive: "Set account IDs after connect for live pulls",
    alertsWebhook: "Notification platform + optional webhook",
    awaitingScan: "Awaiting scan",
    productAnalytics7d: "Last 7 days (product analytics)",
    sessionsTracked: "Browser sessions tracked",
    mostViewedPath: "Most viewed path",
    leadVelocitySrc: "From /api/marketing/analytics/summary",
    oauthConnectors: "OAuth connectors",
    moduleRegistry: "Marketing module registry",
    noAlerts: "No connector alerts. Sync and validate from Integrations.",
    navigateToCollect: "Navigate the app to start collecting product usage.",
    searchMinChars: "Enter at least 2 characters",
  },
  form: {
    name: "Name",
    title: "Title",
    email: "Email",
    phone: "Phone",
    role: "Role",
    tenant: "Tenant",
    organization: "Organization",
    channel: "Channel",
    channels: "Channels",
    tone: "Tone",
    brief: "Brief",
    url: "URL",
    primaryKeyword: "Primary keyword",
    seedKeyword: "Seed keyword",
    company: "Company",
    industry: "Industry",
    reportType: "Report type",
    domain: "Domain",
    pricingTier: "Pricing tier",
    notes: "Notes",
    platform: "Platform",
    topic: "Topic",
    captionOptional: "Caption (optional)",
    status: "Status",
    network: "Network",
    objective: "Objective",
    dailyBudget: "Daily budget (USD)",
    budgetUsd: "Budget (USD)",
    format: "Format",
    slug: "Slug",
    headline: "Headline",
    template: "Template",
    websiteUrl: "Website URL",
    jobType: "Job type",
    trigger: "Trigger",
    brandVoice: "Brand voice",
    defaultLocale: "Default locale",
    timezone: "Timezone",
    alertEmail: "Alert email (optional)",
    alertWebhook: "Alert webhook URL (optional)",
    quietStart: "Quiet start",
    quietEnd: "Quiet end",
  },
  action: {
    save: "Save",
    saving: "Saving…",
    create: "Create",
    creating: "Creating…",
    createCampaign: "Create campaign",
    generateDraft: "Generate draft",
    generating: "Generating…",
    generateReport: "Generate report",
    runAudit: "Run audit",
    runResearch: "Run research",
    researching: "Researching…",
    addCompetitor: "Add competitor",
    createPost: "Queue post",
    createPage: "Create page",
    createCreative: "Create creative",
    createScript: "Generate script",
    createWorkflow: "Create workflow",
    scan: "Scan website",
    scanning: "Scanning…",
    runJob: "Enqueue job",
    running: "Running…",
    saveSettings: "Save settings",
    saveAlertPrefs: "Save alert prefs",
    sendTestAlert: "Send test alert",
    sending: "Sending…",
    flushDigest: "Flush digest",
    flushing: "Flushing…",
    runSchedulerTick: "Run scheduler tick",
    ticking: "Ticking…",
    emailApprovals: "Email notifications for approvals",
    quietHours: "Quiet hours (buffer medium/low alerts)",
    quietStrict: "Strict quiet hours (also buffer high priority)",
    notifySyncFailures: "Notify on sync fallback / validate failures",
    markedAllRead: "All notifications are marked as read",
    markedRead: "Notification marked as read",
    createCrmTask: "Create CRM task",
    createCrmTaskDryRun: "Preview CRM task",
    createCrmLead: "Create CRM lead",
    createCrmLeadDryRun: "Preview CRM lead",
    cancel: "Cancel",
    crmWriteDisabled: "CRM write-back is off (preview only)",
  },
  backlinks: {
    industry: "Industry",
    country: "Country",
    allIndustries: "All industries",
    allCountries: "All countries",
    clearFilters: "Clear filters",
    refresh: "Refresh",
    openSite: "Open site",
    copyLink: "Copy link",
    copied: "Copied",
    loading: "Loading…",
    more: "+{n} more",
    allSites: "All {n} sites",
    someSites: "{shown} of {n} sites",
    hint: "Pick your industry and country, then open a site and publish there yourself. MarketIQ only lists these sites — it never posts on your behalf.",
    emptyTitle: "No sites match these filters",
    emptyDescription: "Try another industry or country, or clear the filters to see all sites.",
    errorTitle: "Could not load the directory",
    errorDescription: "Check your connection, then try again.",
  },
};

export const uiEs: UiTree = {
  count: {
    total: "{n} en total",
    items: "{n} elementos",
    drafts: "{n} borradores",
    keywords: "{n} keywords",
    posts: "{n} publicaciones",
    companies: "{n} empresas",
    assets: "{n} recursos",
    videos: "{n} videos",
    saved: "{n} guardados",
    recorded: "{n} registrados",
    defined: "{n} definidos",
  },
  card: {
    library: "Biblioteca",
    campaigns: "Campañas",
    reports: "Informes",
    results: "Resultados",
    connectors: "Conectores",
    compose: "Redactar",
    watchlist: "Lista de seguimiento",
    scripts: "Guiones",
    pages: "Páginas",
    workflows: "Flujos",
    newCampaign: "Nueva campaña",
    crmLeadFromCampaign: "Lead CRM desde campaña",
    newDraft: "Nuevo borrador",
    newPage: "Nueva página",
    newVideo: "Nuevo video",
    newWorkflow: "Nuevo flujo",
    newImage: "Nueva creatividad de imagen",
    addCompetitor: "Añadir competidor",
    generateReport: "Generar informe",
    runAudit: "Ejecutar auditoría",
    auditHistory: "Historial de auditorías",
    keywordList: "Lista de keywords",
    calendarQueue: "Cola del calendario",
    byNetwork: "Por red",
    scanUrl: "Escanear URL",
    runJob: "Ejecutar trabajo",
    recentJobs: "Trabajos recientes",
    openRecommendations: "Recomendaciones abiertas",
    permissions: "Tus permisos de marketing",
    brandLocale: "Marca e idioma",
    connectorAlerts: "Alertas de conectores",
    usageQuotas: "Uso y cuotas",
    leadsSpend: "Leads y gasto",
    channelMix: "Mezcla de canales",
    conversionFunnel: "Embudo de conversión",
    last14Days: "Últimos 14 días",
    dailyPageViews: "Vistas diarias",
    topPages: "Páginas principales",
    connectorHealth: "Salud de conectores",
    leadVelocity: "Velocidad de leads",
    amosPageViews: "Vistas MarketIQ",
    amosSessions: "Sesiones MarketIQ",
    topModule: "Módulo principal",
    crmLeads: "Leads CRM",
    agentConnectivity: "Conectividad de agentes",
    auditEvents: "Eventos de auditoría",
    platformHealth: "Salud de la plataforma",
    syncMix: "Mezcla de sync",
    searchResults: "Resultados de búsqueda",
  },
  empty: {
    campaigns: {
      title: "Aún no hay campañas",
      description: "Crea un borrador para empezar a orquestar canales.",
    },
    ads: {
      title: "Sin campañas de anuncios",
      description: "Crea un borrador para generar copy creativo.",
    },
    drafts: {
      title: "Aún no hay borradores",
      description: "Genera tu primera pieza desde un título y brief.",
    },
    keywords: {
      title: "Aún no hay keywords",
      description: "Ejecuta investigación con un término semilla para generar un cluster.",
    },
    social: {
      title: "Cola vacía",
      description: "Redacta una publicación para llenar el calendario social.",
    },
    competitors: {
      title: "Aún no hay competidores",
      description: "Añade un dominio para comparar SEO y precios.",
    },
    reports: {
      title: "Aún no hay informes",
      description: "Genera un SWOT o pack de personas para empezar.",
    },
    audits: {
      title: "Aún no hay auditorías",
      description: "Las auditorías registradas en este espacio aparecerán aquí.",
    },
    landing: {
      title: "Sin landing pages",
      description: "Genera un esquema SEO con CTA de formulario.",
    },
    images: {
      title: "Sin creatividades de imagen",
      description: "Crea un brief para encolar producción.",
    },
    videos: {
      title: "Sin guiones de video",
      description: "Genera un guion temporizado desde un brief.",
    },
    recommendations: {
      title: "Sin recomendaciones",
      description: "La actividad en SEO, contenido y anuncios alimentará este feed.",
    },
    jobs: {
      title: "Aún no hay trabajos",
      description: "Ejecuta un refresco CRM o enriquecimiento BI.",
    },
    workflows: {
      title: "Sin flujos",
      description: "Crea una cadena de aprobación para contenido o social.",
    },
    observabilityAlerts: {
      title: "Sin alertas de conectores",
      description: "Avisos de sync y validación aparecerán aquí.",
    },
    observabilityEvents: {
      title: "Sin eventos de auditoría",
      description: "Altas, syncs y write-backs se registran en amos_audit_events.",
    },
    search: {
      title: "Buscar MarketIQ",
      description: "Escribe al menos 2 caracteres para buscar campañas, keywords, borradores y más.",
    },
    searchNone: {
      title: "Sin coincidencias",
      description: "Prueba otro término o crea el activo en su módulo primero.",
    },
  },
  hint: {
    briefSkeleton: "Brief → esqueleto de borrador",
    adsAgent: "Agente de anuncios",
    seoAgent: "Agente SEO",
    businessAgent: "Agente de negocio",
    videoAgent: "Agente de video",
    redisQueue: "Cola Redis cuando esté disponible",
    connectorsLive: "Configura IDs de cuenta tras conectar para pulls en vivo",
    alertsWebhook: "Plataforma de notificaciones + webhook opcional",
    awaitingScan: "Esperando escaneo",
    productAnalytics7d: "Últimos 7 días (analítica de producto)",
    sessionsTracked: "Sesiones de navegador rastreadas",
    mostViewedPath: "Ruta más vista",
    leadVelocitySrc: "Desde /api/marketing/analytics/summary",
    oauthConnectors: "Conectores OAuth",
    moduleRegistry: "Registro de módulos de marketing",
    noAlerts: "Sin alertas de conectores. Sincroniza y valida en Integraciones.",
    navigateToCollect: "Navega la app para empezar a recopilar uso del producto.",
    searchMinChars: "Introduce al menos 2 caracteres",
  },
  form: {
    name: "Nombre",
    title: "Título",
    email: "Correo",
    phone: "Teléfono",
    role: "Rol",
    tenant: "Tenant",
    organization: "Organización",
    channel: "Canal",
    channels: "Canales",
    tone: "Tono",
    brief: "Brief",
    url: "URL",
    primaryKeyword: "Keyword principal",
    seedKeyword: "Keyword semilla",
    company: "Empresa",
    industry: "Industria",
    reportType: "Tipo de informe",
    domain: "Dominio",
    pricingTier: "Nivel de precios",
    notes: "Notas",
    platform: "Plataforma",
    topic: "Tema",
    captionOptional: "Caption (opcional)",
    status: "Estado",
    network: "Red",
    objective: "Objetivo",
    dailyBudget: "Presupuesto diario (USD)",
    budgetUsd: "Presupuesto (USD)",
    format: "Formato",
    slug: "Slug",
    headline: "Titular",
    template: "Plantilla",
    websiteUrl: "URL del sitio",
    jobType: "Tipo de trabajo",
    trigger: "Disparador",
    brandVoice: "Voz de marca",
    defaultLocale: "Locale por defecto",
    timezone: "Zona horaria",
    alertEmail: "Email de alertas (opcional)",
    alertWebhook: "Webhook de alertas (opcional)",
    quietStart: "Inicio silencio",
    quietEnd: "Fin silencio",
  },
  action: {
    save: "Guardar",
    saving: "Guardando…",
    create: "Crear",
    creating: "Creando…",
    createCampaign: "Crear campaña",
    generateDraft: "Generar borrador",
    generating: "Generando…",
    generateReport: "Generar informe",
    runAudit: "Ejecutar auditoría",
    runResearch: "Investigar",
    researching: "Investigando…",
    addCompetitor: "Añadir competidor",
    createPost: "Encolar publicación",
    createPage: "Crear página",
    createCreative: "Crear creatividad",
    createScript: "Generar guion",
    createWorkflow: "Crear flujo",
    scan: "Escanear sitio",
    scanning: "Escaneando…",
    runJob: "Encolar trabajo",
    running: "Ejecutando…",
    saveSettings: "Guardar ajustes",
    saveAlertPrefs: "Guardar prefs. de alertas",
    sendTestAlert: "Enviar alerta de prueba",
    sending: "Enviando…",
    flushDigest: "Vaciar digest",
    flushing: "Vaciando…",
    runSchedulerTick: "Ejecutar tick del scheduler",
    ticking: "Ejecutando…",
    emailApprovals: "Notificaciones por email para aprobaciones",
    quietHours: "Horario silencioso (buffer alertas medias/bajas)",
    quietStrict: "Silencio estricto (también buffer alta prioridad)",
    notifySyncFailures: "Notificar fallback de sync / fallos de validación",
    markedAllRead: "Todas las notificaciones están marcadas como leídas",
    markedRead: "Notificación marcada como leída",
    createCrmTask: "Crear tarea CRM",
    createCrmTaskDryRun: "Vista previa de tarea CRM",
    createCrmLead: "Crear lead CRM",
    createCrmLeadDryRun: "Vista previa de lead CRM",
    cancel: "Cancelar",
    crmWriteDisabled: "Write-back CRM desactivado (solo vista previa)",
  },
  backlinks: {
    industry: "Industria",
    country: "País",
    allIndustries: "Todas las industrias",
    allCountries: "Todos los países",
    clearFilters: "Limpiar filtros",
    refresh: "Actualizar",
    openSite: "Abrir sitio",
    copyLink: "Copiar enlace",
    copied: "Copiado",
    loading: "Cargando…",
    more: "+{n} más",
    allSites: "Los {n} sitios",
    someSites: "{shown} de {n} sitios",
    hint: "Elige tu industria y país, luego abre un sitio y publica allí tú mismo. MarketIQ solo lista estos sitios: nunca publica por ti.",
    emptyTitle: "Ningún sitio coincide con estos filtros",
    emptyDescription: "Prueba otra industria o país, o limpia los filtros para ver todos los sitios.",
    errorTitle: "No se pudo cargar el directorio",
    errorDescription: "Comprueba tu conexión y vuelve a intentarlo.",
  },
};

export const uiHi: UiTree = {
  count: {
    total: "{n} कुल",
    items: "{n} आइटम",
    drafts: "{n} ड्राफ्ट",
    keywords: "{n} कीवर्ड",
    posts: "{n} पोस्ट",
    companies: "{n} कंपनियाँ",
    assets: "{n} एसेट",
    videos: "{n} वीडियो",
    saved: "{n} सेव्ड",
    recorded: "{n} रिकॉर्डेड",
    defined: "{n} परिभाषित",
  },
  card: {
    library: "लाइब्रेरी",
    campaigns: "कैंपेन",
    reports: "रिपोर्ट",
    results: "परिणाम",
    connectors: "कनेक्टर",
    compose: "कंपोज़",
    watchlist: "वॉचलिस्ट",
    scripts: "स्क्रिप्ट",
    pages: "पेज",
    workflows: "वर्कफ़्लो",
    newCampaign: "नया कैंपेन",
    crmLeadFromCampaign: "कैंपेन से CRM लीड",
    newDraft: "नया ड्राफ्ट",
    newPage: "नया पेज",
    newVideo: "नया वीडियो",
    newWorkflow: "नया वर्कफ़्लो",
    newImage: "नया इमेज क्रिएटिव",
    addCompetitor: "प्रतिस्पर्धी जोड़ें",
    generateReport: "रिपोर्ट बनाएँ",
    runAudit: "ऑडिट चलाएँ",
    auditHistory: "ऑडिट इतिहास",
    keywordList: "कीवर्ड सूची",
    calendarQueue: "कैलेंडर क्यू",
    byNetwork: "नेटवर्क अनुसार",
    scanUrl: "URL स्कैन करें",
    runJob: "जॉब चलाएँ",
    recentJobs: "हाल की जॉब्स",
    openRecommendations: "खुली सिफ़ारिशें",
    permissions: "आपकी मार्केटिंग अनुमतियाँ",
    brandLocale: "ब्रांड और लोकेल",
    connectorAlerts: "कनेक्टर अलर्ट",
    usageQuotas: "उपयोग और कोटा",
    leadsSpend: "लीड और खर्च",
    channelMix: "चैनल मिक्स",
    conversionFunnel: "कन्वर्ज़न फ़नल",
    last14Days: "पिछले 14 दिन",
    dailyPageViews: "दैनिक पेज व्यू",
    topPages: "टॉप पेज",
    connectorHealth: "कनेक्टर हेल्थ",
    leadVelocity: "लीड वेलोसिटी",
    amosPageViews: "MarketIQ पेज व्यू",
    amosSessions: "MarketIQ सेशन",
    topModule: "टॉप मॉड्यूल",
    crmLeads: "CRM लीड",
    agentConnectivity: "एजेंट कनेक्टिविटी",
    auditEvents: "ऑडिट इवेंट्स",
    platformHealth: "प्लेटफ़ॉर्म हेल्थ",
    syncMix: "सिंक मिक्स",
    searchResults: "खोज परिणाम",
  },
  empty: {
    campaigns: {
      title: "अभी कोई कैंपेन नहीं",
      description: "चैनल ऑर्केस्ट्रेट करने के लिए ड्राफ्ट कैंपेन बनाएँ।",
    },
    ads: {
      title: "कोई ऐड कैंपेन नहीं",
      description: "क्रिएटिव कॉपी के लिए ड्राफ्ट बनाएँ।",
    },
    drafts: {
      title: "अभी कोई ड्राफ्ट नहीं",
      description: "टाइटल और ब्रीफ से पहला पीस जनरेट करें।",
    },
    keywords: {
      title: "अभी कोई कीवर्ड नहीं",
      description: "क्लस्टर के लिए सीड टर्म से रिसर्च चलाएँ।",
    },
    social: {
      title: "क्यू खाली",
      description: "सोशल कैलेंडर भरने के लिए पोस्ट लिखें।",
    },
    competitors: {
      title: "अभी कोई प्रतिस्पर्धी नहीं",
      description: "SEO और प्राइसिंग तुलना के लिए डोमेन जोड़ें।",
    },
    reports: {
      title: "अभी कोई रिपोर्ट नहीं",
      description: "शुरू करने के लिए SWOT या पर्सना पैक बनाएँ।",
    },
    audits: {
      title: "अभी कोई ऑडिट नहीं",
      description: "इस वर्कस्पेस में रिकॉर्ड किए गए ऑडिट यहाँ दिखेंगे।",
    },
    landing: {
      title: "कोई लैंडिंग पेज नहीं",
      description: "फ़ॉर्म CTA के साथ SEO-फ्रेंडली आउटलाइन बनाएँ।",
    },
    images: {
      title: "कोई इमेज क्रिएटिव नहीं",
      description: "प्रोडक्शन क्यू के लिए ब्रीफ बनाएँ।",
    },
    videos: {
      title: "कोई वीडियो स्क्रिप्ट नहीं",
      description: "ब्रीफ से टाइम्ड स्क्रिप्ट जनरेट करें।",
    },
    recommendations: {
      title: "कोई सिफ़ारिश नहीं",
      description: "SEO, कंटेंट और ऐड्स की गतिविधि यह फ़ीड भरेगी।",
    },
    jobs: {
      title: "अभी कोई जॉब नहीं",
      description: "CRM रिफ़्रेश या BI enrich जॉब चलाएँ।",
    },
    workflows: {
      title: "कोई वर्कफ़्लो नहीं",
      description: "कंटेंट या सोशल के लिए अप्रूवल चेन बनाएँ।",
    },
    observabilityAlerts: {
      title: "कोई कनेक्टर अलर्ट नहीं",
      description: "सिंक चेतावनियाँ और वैलिडेशन समस्याएँ यहाँ दिखेंगी।",
    },
    observabilityEvents: {
      title: "कोई ऑडिट इवेंट नहीं",
      description: "क्रिएट, सिंक और राइट-बैक amos_audit_events में लॉग होते हैं।",
    },
    search: {
      title: "MarketIQ खोजें",
      description: "कैंपेन, कीवर्ड, ड्राफ्ट आदि खोजने के लिए कम से कम 2 अक्षर लिखें।",
    },
    searchNone: {
      title: "कोई मिलान नहीं",
      description: "दूसरा शब्द आज़माएँ या पहले मॉड्यूल में एसेट बनाएँ।",
    },
  },
  hint: {
    briefSkeleton: "ब्रीफ → ड्राफ्ट स्केलेटन",
    adsAgent: "Ads Agent",
    seoAgent: "SEO Agent",
    businessAgent: "Business Agent",
    videoAgent: "Video Agent",
    redisQueue: "उपलब्ध होने पर Redis क्यू",
    connectorsLive: "लाइव पुल के लिए कनेक्ट के बाद अकाउंट ID सेट करें",
    alertsWebhook: "नोटिफ़िकेशन प्लेटफ़ॉर्म + वैकल्पिक वेबहुक",
    awaitingScan: "स्कैन की प्रतीक्षा",
    productAnalytics7d: "पिछले 7 दिन (प्रोडक्ट एनालिटिक्स)",
    sessionsTracked: "ट्रैक किए ब्राउज़र सेशन",
    mostViewedPath: "सबसे ज़्यादा देखी गई पाथ",
    leadVelocitySrc: "/api/marketing/analytics/summary से",
    oauthConnectors: "OAuth कनेक्टर",
    moduleRegistry: "मार्केटिंग मॉड्यूल रजिस्ट्री",
    noAlerts: "कोई कनेक्टर अलर्ट नहीं। Integrations से सिंक/वैलिडेट करें।",
    navigateToCollect: "प्रोडक्ट यूसेज इकट्ठा करने के लिए ऐप में नेविगेट करें।",
    searchMinChars: "कम से कम 2 अक्षर लिखें",
  },
  form: {
    name: "नाम",
    title: "टाइटल",
    email: "ईमेल",
    phone: "फ़ोन",
    role: "भूमिका",
    tenant: "टेनेंट",
    organization: "संगठन",
    channel: "चैनल",
    channels: "चैनल",
    tone: "टोन",
    brief: "ब्रीफ",
    url: "URL",
    primaryKeyword: "प्राथमिक कीवर्ड",
    seedKeyword: "सीड कीवर्ड",
    company: "कंपनी",
    industry: "उद्योग",
    reportType: "रिपोर्ट प्रकार",
    domain: "डोमेन",
    pricingTier: "प्राइसिंग टियर",
    notes: "नोट्स",
    platform: "प्लेटफ़ॉर्म",
    topic: "टॉपिक",
    captionOptional: "कैप्शन (वैकल्पिक)",
    status: "स्थिति",
    network: "नेटवर्क",
    objective: "उद्देश्य",
    dailyBudget: "दैनिक बजट (USD)",
    budgetUsd: "बजट (USD)",
    format: "फ़ॉर्मेट",
    slug: "स्लग",
    headline: "हेडलाइन",
    template: "टेम्प्लेट",
    websiteUrl: "वेबसाइट URL",
    jobType: "जॉब प्रकार",
    trigger: "ट्रिगर",
    brandVoice: "ब्रांड वॉइस",
    defaultLocale: "डिफ़ॉल्ट लोकेल",
    timezone: "टाइमज़ोन",
    alertEmail: "अलर्ट ईमेल (वैकल्पिक)",
    alertWebhook: "अलर्ट वेबहुक URL (वैकल्पिक)",
    quietStart: "क्वायट शुरू",
    quietEnd: "क्वायट अंत",
  },
  action: {
    save: "सेव",
    saving: "सेव हो रहा है…",
    create: "बनाएँ",
    creating: "बन रहा है…",
    createCampaign: "कैंपेन बनाएँ",
    generateDraft: "ड्राफ्ट जनरेट करें",
    generating: "जनरेट हो रहा है…",
    generateReport: "रिपोर्ट बनाएँ",
    runAudit: "ऑडिट चलाएँ",
    runResearch: "रिसर्च चलाएँ",
    researching: "रिसर्च हो रही है…",
    addCompetitor: "प्रतिस्पर्धी जोड़ें",
    createPost: "पोस्ट क्यू करें",
    createPage: "पेज बनाएँ",
    createCreative: "क्रिएटिव बनाएँ",
    createScript: "स्क्रिप्ट जनरेट करें",
    createWorkflow: "वर्कफ़्लो बनाएँ",
    scan: "वेबसाइट स्कैन करें",
    scanning: "स्कैन हो रहा है…",
    runJob: "जॉब क्यू करें",
    running: "चल रहा है…",
    saveSettings: "सेटिंग्स सेव करें",
    saveAlertPrefs: "अलर्ट प्रेफ़्स सेव करें",
    sendTestAlert: "टेस्ट अलर्ट भेजें",
    sending: "भेजा जा रहा है…",
    flushDigest: "डाइजेस्ट फ़्लश करें",
    flushing: "फ़्लश हो रहा है…",
    runSchedulerTick: "शेड्यूलर टिक चलाएँ",
    ticking: "टिक चल रहा है…",
    emailApprovals: "अप्रूवल के लिए ईमेल नोटिफ़िकेशन",
    quietHours: "क्वायट आवर्स (मध्यम/कम अलर्ट बफ़र)",
    quietStrict: "सख्त क्वायट आवर्स (हाई प्रायोरिटी भी बफ़र)",
    notifySyncFailures: "सिंक फ़ॉलबैक / वैलिडेट फ़ेल पर सूचित करें",
    markedAllRead: "सभी नोटिफ़िकेशन पढ़े गए के रूप में मार्क कर दिए गए हैं",
    markedRead: "नोटिफ़िकेशन पढ़ा गया के रूप में मार्क किया गया",
    createCrmTask: "CRM टास्क बनाएँ",
    createCrmTaskDryRun: "CRM टास्क प्रीव्यू",
    createCrmLead: "CRM लीड बनाएँ",
    createCrmLeadDryRun: "CRM लीड प्रीव्यू",
    cancel: "रद्द करें",
    crmWriteDisabled: "CRM राइट-बैक बंद है (केवल प्रीव्यू)",
  },
  backlinks: {
    industry: "उद्योग",
    country: "देश",
    allIndustries: "सभी उद्योग",
    allCountries: "सभी देश",
    clearFilters: "फ़िल्टर हटाएँ",
    refresh: "रिफ़्रेश करें",
    openSite: "साइट खोलें",
    copyLink: "लिंक कॉपी करें",
    copied: "कॉपी हो गया",
    loading: "लोड हो रहा है…",
    more: "+{n} और",
    allSites: "सभी {n} साइट",
    someSites: "{n} में से {shown} साइट",
    hint: "अपना उद्योग और देश चुनें, फिर साइट खोलकर वहाँ खुद पब्लिश करें। MarketIQ सिर्फ़ इन साइटों की सूची दिखाता है — आपकी ओर से कभी पोस्ट नहीं करता।",
    emptyTitle: "इन फ़िल्टरों से कोई साइट नहीं मिली",
    emptyDescription: "कोई और उद्योग या देश आज़माएँ, या सभी साइट देखने के लिए फ़िल्टर हटाएँ।",
    errorTitle: "डायरेक्टरी लोड नहीं हो सकी",
    errorDescription: "कनेक्शन जाँचें, फिर दोबारा कोशिश करें।",
  },
};
